import type {
  ContentItem,
  ExamType,
} from '../../domain/entities/content.entity';
import { EXAM_TYPE_LABELS } from '../display';
import { formatInstitution } from '../exam-body';
import { useInstitutionsById } from '../hooks/use-institutions';
import { AnnulledNotice } from './AnnulledNotice';
import { sanitizeQuestionPreview } from '../rich-text';
import { tokensToEditorHtml } from '../question-images';

/**
 * Visualização (somente leitura) do body por tipo. Os campos de texto rico são
 * renderizados como HTML sanitizado; os demais como texto/links simples.
 *
 * O body vem com os tokens `{{img:<fileId>}}` (é a mesma ficha que a edição
 * usa), e cada texto rico os troca pela imagem com `imageUrlsByFileId`.
 */
export function BodyView({
  item,
  imageUrlsByFileId,
}: {
  item: ContentItem;
  imageUrlsByFileId: Record<string, string>;
}) {
  const b = asRecord(item.body);
  const rich = (html: string) => (
    <ResolvedHtml html={html} imageUrlsByFileId={imageUrlsByFileId} />
  );

  switch (item.type) {
    case 'FLASHCARD':
      return (
        <div className="space-y-4">
          <ReadField label="Frente">{rich(str(b.front))}</ReadField>
          <ReadField label="Verso">{rich(str(b.back))}</ReadField>
        </div>
      );

    case 'OFFICIAL_COMMENT':
      return rich(str(b.text));

    case 'QUESTION': {
      const isMc = b.format === 'MULTIPLE_CHOICE';
      const alternatives = Array.isArray(b.alternatives)
        ? (b.alternatives as unknown[]).map(asRecord)
        : [];
      const correctId = str(b.correctAlternativeId);
      // Como o `QuestionBodyView`: a anulada não pinta letra nenhuma de verde,
      // mesmo com a letra guardada para quando for desanulada.
      const annulled = isMc && b.annulled === true;
      return (
        <div className="space-y-4">
          {annulled && <AnnulledNotice />}
          <ReadField label="Enunciado">{rich(str(b.stem))}</ReadField>
          {isMc && (
            <ReadField label="Alternativas">
              <ul className="space-y-2">
                {alternatives.map((alt, i) => {
                  const correct = !annulled && str(alt.id) === correctId;
                  return (
                    <li
                      key={str(alt.id) || i}
                      className={[
                        'flex items-start gap-2 rounded-lg border p-2',
                        correct
                          ? 'border-green-500/50 bg-green-500/10'
                          : 'border-(--color-border)',
                      ].join(' ')}
                    >
                      <span className="text-(--color-text-secondary) pt-0.5 text-sm font-medium">
                        {str(alt.id).toUpperCase()}
                      </span>
                      <div className="flex-1">{rich(str(alt.text))}</div>
                      {correct && (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">
                          correta
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </ReadField>
          )}
          {!isMc && str(b.expectedAnswer) && (
            <ReadField label="Resposta esperada">
              {rich(str(b.expectedAnswer))}
            </ReadField>
          )}
        </div>
      );
    }

    case 'VIDEO':
      return (
        <div className="space-y-4">
          <ReadField label="URL">
            {str(b.url) ? (
              <a
                href={str(b.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-(--color-accent) break-all underline"
              >
                {str(b.url)}
              </a>
            ) : (
              <Empty />
            )}
          </ReadField>
          {typeof b.durationSec === 'number' && (
            <ReadField label="Duração (s)">{b.durationSec}</ReadField>
          )}
          {b.transcript ? (
            <ReadField label="Transcrição">
              <p className="text-(--color-text-primary) whitespace-pre-wrap text-sm">
                {str(b.transcript)}
              </p>
            </ReadField>
          ) : null}
        </div>
      );

    case 'EXAM':
      return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ReadField label="Ano">{num(b.year) || <Empty />}</ReadField>
          <ReadField label="Instituição">
            {str(b.institutionId) ? (
              <ExamInstitution institutionId={str(b.institutionId)} />
            ) : (
              <Empty />
            )}
          </ReadField>
          <ReadField label="Variante">{str(b.variant) || <Empty />}</ReadField>
          <ReadField label="Tipo da prova">
            {EXAM_TYPE_LABELS[b.examType as ExamType] ?? <Empty />}
          </ReadField>
        </div>
      );

    default:
      return (
        <pre className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) max-h-96 overflow-auto rounded-lg border p-4 text-xs">
          {JSON.stringify(item.body, null, 2)}
        </pre>
      );
  }
}

function ResolvedHtml({
  html,
  imageUrlsByFileId,
}: {
  html: string;
  imageUrlsByFileId: Record<string, string>;
}) {
  return (
    <div
      // `rich-text-read`: a figura no tamanho de leitura, como no card da prova.
      className="rich-text rich-text-read text-(--color-text-primary) text-sm"
      // Sanitizado no cliente (allowlist do back + `img`, que só existe aqui
      // porque o token acabou de virar imagem) antes de renderizar.
      dangerouslySetInnerHTML={{
        __html: sanitizeQuestionPreview(
          tokensToEditorHtml(html, imageUrlsByFileId),
        ),
      }}
    />
  );
}

function ReadField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <span className="text-(--color-text-secondary) block text-xs font-medium uppercase tracking-wide">
        {label}
      </span>
      <div className="text-(--color-text-primary) text-sm">{children}</div>
    </div>
  );
}

function Empty() {
  return <span className="text-(--color-text-secondary)">—</span>;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function num(value: unknown): string {
  return typeof value === 'number' ? String(value) : '';
}

/**
 * `(SIGLA-UF) NOME`, como no preview da prova. O id só aparece quando a
 * instituição não existe na lista já carregada — carregando, não pisca o UUID.
 */
function ExamInstitution({ institutionId }: { institutionId: string }) {
  const { data: byId, isLoading } = useInstitutionsById();
  if (isLoading) return <Empty />;
  const name = byId?.get(institutionId)?.name;
  return <span title={name}>{formatInstitution(institutionId, byId)}</span>;
}
